/**
 * Requirement Inspector — right-side drawer with 9 sections (plan §24.4.3).
 *
 * Triggered by clicking any card on the board. Renders the FULL contents of
 * one ComplianceRequirementCard at OEM-grade information density —
 * deliberately heavier than the compact lane card. This is the answer to
 * Kaiyang Lesson §25.3 / round-2 feedback ("信息密度不够"): lane cards stay
 * scannable, drawer carries the detail.
 *
 * Sections (plan §24.4.3 wireframe):
 *   §1  Node facts
 *   §2  Why applies
 *   §3  Related features (annotation only)
 *   §4  Evidence guidance
 *   §5  Linked obligations
 *   §6  Coverage and validation
 *   §7  Legal references
 *   §7b Continuing obligation — CONDITIONAL on obligationTiming ∈ {
 *       post_market, recurring, event_triggered, phase_in }
 *   §8  Technical metadata
 *   §9  Audit trail
 *
 * A11y (plan §24.10):
 *   - role="dialog"
 *   - aria-modal="true"
 *   - focus trap inside drawer
 *   - return focus to triggering element on close
 *   - Esc closes
 */

import { useEffect, useRef } from "react";
import type { ComplianceRequirementCard } from "@regpulse/contracts";

const TIMING_WITH_CONTINUING = new Set([
  "post_market",
  "recurring",
  "event_triggered",
  "phase_in",
]);

const KIND_LABEL: Record<ComplianceRequirementCard["obligationKind"], string> = {
  hard_law: "Hard law",
  evidence_standard: "Evidence standard",
  market_force: "Non-legal · Market force",
  ecosystem_signal: "Non-legal · Ecosystem",
};

const KIND_COLOR: Record<ComplianceRequirementCard["obligationKind"], string> = {
  hard_law: "var(--ft-hard-law)",
  evidence_standard: "var(--ft-evidence)",
  market_force: "var(--ft-market-force)",
  ecosystem_signal: "var(--ft-ecosystem)",
};

interface Props {
  card: ComplianceRequirementCard;
  outcomeReason?: string;
  onClose: () => void;
}

export function RequirementInspector({ card, outcomeReason, onClose }: Props) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeBtnRef.current?.focus();
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
      if (e.key === "Tab" && drawerRef.current) {
        const focusables = drawerRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const showContinuing = TIMING_WITH_CONTINUING.has(card.obligationTiming);
  const rail = KIND_COLOR[card.obligationKind];

  return (
    <>
      <div
        data-testid="inspector-backdrop"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "var(--color-overlay)",
          zIndex: 100,
        }}
      />
      <aside
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="inspector-title"
        data-testid="requirement-inspector"
        data-card-id={card.id}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(720px, 92vw)",
          background: "var(--color-surface-elevated)",
          borderLeft: `4px solid ${rail}`,
          boxShadow: "var(--shadow-modal)",
          zIndex: 101,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        {/* ── Header ──────────────────────────────────────────────── */}
        <header
          style={{
            position: "sticky",
            top: 0,
            background: "var(--color-surface-elevated)",
            padding: "var(--space-16)",
            borderBottom: "1px solid var(--color-border)",
            zIndex: 1,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-12)" }}>
            <div style={{ minWidth: 0 }}>
              <p
                className="type-caption"
                style={{ margin: 0, color: "var(--color-text-secondary)" }}
              >
                Requirement · {card.family}
              </p>
              <h2
                id="inspector-title"
                className="type-h2"
                style={{ margin: "var(--space-4) 0 0 0" }}
              >
                {card.title}
              </h2>
              <div
                style={{
                  marginTop: "var(--space-8)",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "var(--space-4)",
                  fontSize: 11,
                }}
              >
                <Chip color={rail} label={KIND_LABEL[card.obligationKind]} />
                <Chip label={`Layer ${card.layer}`} />
                <Chip label={card.obligationTiming} />
                <Chip label={card.legalEffect} />
                <Chip label={card.lifecycleState} />
                <Chip label={`tier ${card.verificationMaturityTier}`} />
              </div>
              <p
                className="type-caption"
                style={{ margin: "var(--space-8) 0 0 0", color: "var(--color-text-secondary)" }}
              >
                Snapshot: {card.lastUpdate} · {card.applicabilityCategory} ·{" "}
                {card.validationStatus}
              </p>
            </div>
            <button
              ref={closeBtnRef}
              onClick={onClose}
              aria-label="Close requirement inspector"
              data-testid="inspector-close"
              style={{
                width: 32,
                height: 32,
                border: "1px solid var(--color-border)",
                background: "var(--color-surface-sunken)",
                borderRadius: "var(--radius-4)",
                cursor: "pointer",
                fontSize: 18,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        </header>

        {/* ── Body ────────────────────────────────────────────────── */}
        <div style={{ padding: "var(--space-16)", display: "grid", gap: "var(--space-24)" }}>
          <Section id="1" title="Node facts" testId="section-1">
            <Dl rows={[
              ["id", <code key="id">{card.id}</code>],
              ["domain", card.regulatoryDomain],
              ["jurisdiction", card.jurisdictionCode],
              ["family", card.family],
              ["layer", card.layer],
              ["lifecycle state", card.lifecycleState],
              ["maturity tier", card.verificationMaturityTier],
              ["confidence", `${card.confidence}`],
              ["source system", card.sourceSystem],
              ["last update", card.lastUpdate],
            ]} />
          </Section>

          <Section id="2" title="Why applies" testId="section-2">
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
              {card.whyAppliesToVehicle || (
                <em style={{ color: "var(--color-text-secondary)" }}>
                  No narrative explanation supplied by the rule.
                </em>
              )}
            </p>
            {card.triggeredByConfigItems.length > 0 && (
              <div style={{ marginTop: "var(--space-8)" }}>
                <p className="type-caption" style={{ margin: "0 0 var(--space-4) 0" }}>
                  Matched config fields
                </p>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                  {card.triggeredByConfigItems.map((c) => (
                    <li key={c}>
                      <code>{c}</code>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {outcomeReason && (
              <p
                className="type-caption"
                style={{
                  marginTop: "var(--space-8)",
                  fontStyle: "italic",
                  color: "var(--color-text-secondary)",
                }}
              >
                Evaluator: {outcomeReason}
              </p>
            )}
          </Section>

          <Section id="3" title="Related features" subtitle="annotation only" testId="section-3">
            {card.triggeredByFeatures.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-4)" }}>
                {card.triggeredByFeatures.map((f) => (
                  <Chip key={f} label={f} mono />
                ))}
              </div>
            ) : (
              <EmptyHint>No related features tagged.</EmptyHint>
            )}
          </Section>

          <Section id="4" title="Evidence guidance" testId="section-4">
            {card.evidenceGuidance.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none", display: "grid", gap: "var(--space-8)" }}>
                {card.evidenceGuidance.map((e, i) => (
                  <li
                    key={i}
                    style={{
                      padding: "var(--space-8)",
                      border: "1px solid var(--color-border)",
                      borderRadius: "var(--radius-4)",
                      background: "var(--color-surface)",
                    }}
                  >
                    <p style={{ margin: 0, fontWeight: 600, fontSize: 13 }}>{e.description}</p>
                    <div
                      style={{
                        marginTop: 4,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "var(--space-4)",
                        fontSize: 11,
                        color: "var(--color-text-secondary)",
                      }}
                    >
                      {e.artifactKind && <span>artifact: {e.artifactKind}</span>}
                      {e.standardReference && <span>standard: {e.standardReference}</span>}
                      {typeof e.sourceLegalReferenceIndex === "number" && (
                        <span>→ §7 ref [{e.sourceLegalReferenceIndex}]</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyHint>No structured evidence guidance for this rule yet.</EmptyHint>
            )}
          </Section>

          <Section id="5" title="Linked obligations" testId="section-5">
            {card.linkedObligations.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                {card.linkedObligations.map((l) => (
                  <li key={l}>
                    <code>{l}</code>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyHint>This requirement is not chained to other obligations.</EmptyHint>
            )}
          </Section>

          <Section id="6" title="Coverage and validation" testId="section-6">
            <Dl rows={[
              ["coverage state", <strong key="c">{card.coverageState}</strong>],
              ["next action", card.nextCoverageAction || "—"],
              ["coverage gap", card.coverageGapReason ?? "—"],
              ["requirement", card.requirementText || "—"],
              ["execution", card.executionRequirement || "—"],
            ]} />
          </Section>

          <Section id="7" title="Legal references" testId="section-7">
            {card.legalReferences.length > 0 ? (
              <ol
                style={{
                  margin: 0,
                  paddingLeft: 18,
                  display: "grid",
                  gap: "var(--space-8)",
                }}
              >
                {card.legalReferences.map((r, i) => (
                  <li
                    key={i}
                    style={{
                      padding: "var(--space-8)",
                      border: "1px solid var(--color-border)",
                      borderRadius: "var(--radius-4)",
                      fontSize: 12,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-8)" }}>
                      <div style={{ minWidth: 0 }}>
                        <strong>{r.instrument}</strong>
                        {r.article && <> · art. {r.article}</>}
                        {r.annex && <> · annex {r.annex}</>}
                        {r.section && <> · §{r.section}</>}
                        {r.paragraph && <> · ¶{r.paragraph}</>}
                      </div>
                      <span
                        className="type-mono"
                        style={{ fontSize: 10, color: "var(--color-text-secondary)" }}
                      >
                        granularity: {r.granularity} · conf {r.confidence}
                      </span>
                    </div>
                    {r.uri && (
                      <a
                        href={r.uri}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: 11,
                          color: "var(--color-accent)",
                          wordBreak: "break-all",
                        }}
                      >
                        {r.uri} ↗
                      </a>
                    )}
                    {r.citationGapReason && (
                      <p
                        className="type-caption"
                        style={{
                          marginTop: 4,
                          color: "var(--status-candidate-pending)",
                          fontStyle: "italic",
                        }}
                      >
                        gap: {r.citationGapReason}
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            ) : (
              <EmptyHint>
                No structured citations yet — placeholder rule. Verification tier should be
                SEED_UNVERIFIED.
              </EmptyHint>
            )}
          </Section>

          {showContinuing && card.continuingObligation && (
            <Section
              id="7b"
              title="Continuing obligation"
              subtitle={`post-market · ${card.obligationTiming}`}
              testId="section-7b"
              accent="var(--ft-continuing)"
            >
              <Dl rows={[
                ["report name", card.continuingObligation.reportName ?? "—"],
                ["legal basis", card.continuingObligation.legalBasis],
                ["trigger / frequency", card.continuingObligation.triggerOrFrequency],
                ["next due window", card.continuingObligation.nextDueWindow ?? "—"],
                ["monitoring signal", card.continuingObligation.monitoringSignal ?? "—"],
              ]} />
              {card.continuingObligation.requiredRecords.length > 0 && (
                <div style={{ marginTop: "var(--space-8)" }}>
                  <p className="type-caption" style={{ margin: "0 0 var(--space-4) 0" }}>
                    Required records
                  </p>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                    {card.continuingObligation.requiredRecords.map((rec) => (
                      <li key={rec}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
              {card.continuingObligation.evidenceToMaintain.length > 0 && (
                <div style={{ marginTop: "var(--space-8)" }}>
                  <p className="type-caption" style={{ margin: "0 0 var(--space-4) 0" }}>
                    Evidence to maintain
                  </p>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                    {card.continuingObligation.evidenceToMaintain.map((rec) => (
                      <li key={rec}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Section>
          )}

          <Section id="8" title="Technical metadata" testId="section-8">
            <Dl rows={[
              ["ingestion source", card.sourceSystem],
              ["last update", card.lastUpdate],
              ["confidence", `${card.confidence}`],
              ["maturity tier", card.verificationMaturityTier],
              ["verification note", card.verificationNote ?? "—"],
              ["evidence refs", (card.evidenceRefIds ?? []).join(", ") || "—"],
              ["reviewer decisions", (card.reviewerDecisionIds ?? []).join(", ") || "—"],
              ["related inquiries", (card.relatedInquiryIds ?? []).join(", ") || "—"],
            ]} />
          </Section>

          <Section id="9" title="Audit trail" testId="section-9">
            <p style={{ margin: 0, fontSize: 12, lineHeight: 1.6 }}>
              validation: <strong>{card.validationStatus}</strong> · applicability:{" "}
              <strong>{card.applicabilityCategory}</strong>
            </p>
            <EmptyHint>
              Full state-machine timeline (candidate → reviewed → canonical) lands in W7 when
              Review Durable Object goes live (ADR-0002 §11).
            </EmptyHint>
          </Section>
        </div>

        {/* ── Action bar (W10 stubs) ─────────────────────────────── */}
        <footer
          style={{
            position: "sticky",
            bottom: 0,
            background: "var(--color-surface-elevated)",
            borderTop: "1px solid var(--color-border)",
            padding: "var(--space-12) var(--space-16)",
            display: "flex",
            gap: "var(--space-8)",
            flexWrap: "wrap",
          }}
        >
          {[
            "Add note",
            "Attach evidence ref",
            "Open inquiry",
            "Mark for re-review",
          ].map((label) => (
            <button
              key={label}
              disabled
              title="Available when the Review Durable Object lands (W7)."
              style={{
                fontSize: 12,
                padding: "var(--space-4) var(--space-12)",
                border: "1px solid var(--color-border)",
                background: "var(--color-surface-sunken)",
                color: "var(--color-text-secondary)",
                borderRadius: "var(--radius-4)",
                cursor: "not-allowed",
              }}
            >
              {label}
            </button>
          ))}
        </footer>
      </aside>
    </>
  );
}

function Section({
  id,
  title,
  subtitle,
  accent,
  testId,
  children,
}: {
  id: string;
  title: string;
  subtitle?: string;
  accent?: string;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      data-testid={testId}
      aria-labelledby={`section-${id}-heading`}
      style={{
        borderLeft: accent ? `3px solid ${accent}` : undefined,
        paddingLeft: accent ? "var(--space-12)" : undefined,
      }}
    >
      <h3
        id={`section-${id}-heading`}
        className="type-h3"
        style={{
          margin: 0,
          fontSize: 13,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: "var(--color-text-secondary)",
        }}
      >
        §{id} {title}
        {subtitle && (
          <span style={{ fontWeight: 400, marginLeft: 8, textTransform: "none" }}>
            · {subtitle}
          </span>
        )}
      </h3>
      <div style={{ marginTop: "var(--space-8)" }}>{children}</div>
    </section>
  );
}

function Dl({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  return (
    <dl
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: "var(--space-4) var(--space-12)",
        margin: 0,
        fontSize: 12,
      }}
    >
      {rows.map(([k, v]) => (
        <FragmentRow key={k} k={k} v={v} />
      ))}
    </dl>
  );
}

function FragmentRow({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <>
      <dt style={{ color: "var(--color-text-secondary)" }}>{k}</dt>
      <dd style={{ margin: 0, minWidth: 0, wordBreak: "break-word" }}>{v}</dd>
    </>
  );
}

function Chip({ label, color, mono = false }: { label: string; color?: string; mono?: boolean }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px var(--space-8)",
        border: `1px solid ${color ?? "var(--color-border-strong)"}`,
        background: color ? `${color}1A` : "var(--color-surface-sunken)",
        color: color ?? "var(--color-text-secondary)",
        borderRadius: "var(--radius-4)",
        fontSize: 11,
        fontFamily: mono ? "var(--font-mono)" : undefined,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="type-caption"
      style={{
        margin: "var(--space-4) 0 0 0",
        fontStyle: "italic",
        color: "var(--color-text-secondary)",
      }}
    >
      {children}
    </p>
  );
}
